import styled from 'styled-components';

// File-type icon helpers ─────────────────────────────────────────────────
// Resolves a stable "kind" from a mime type + optional filename, then
// renders a small stroke icon. Used in the recipient view + create-flow
// review (and any other place attachments appear).

type FileKind = 'image' | 'video' | 'audio' | 'text' | 'archive' | 'pdf' | 'file';

const ARCHIVE_EXTS = ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz'];
const TEXT_EXTS = ['txt', 'md', 'rtf', 'csv', 'log'];

export function fileIconKind(mime: string | null | undefined, name?: string | null): FileKind {
  const m = (mime ?? '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('text/')) return 'text';
  if (m.includes('zip') || m.includes('compressed') || m.includes('tar') || m.includes('gzip')) return 'archive';

  // Fall back to extension when the mime is generic/missing.
  const ext = (name ?? '').toLowerCase().split('.').pop() ?? '';
  if (ARCHIVE_EXTS.includes(ext)) return 'archive';
  if (TEXT_EXTS.includes(ext)) return 'text';
  if (ext === 'pdf') return 'pdf';
  if (['jpg','jpeg','png','gif','webp','heic','heif','avif','svg','bmp','tiff'].includes(ext)) return 'image';
  if (['mp4','mov','webm','mkv','avi','m4v'].includes(ext)) return 'video';
  if (['mp3','wav','m4a','flac','ogg','aac'].includes(ext)) return 'audio';
  return 'file';
}

export function FileIcon({ kind, size = 22, color = 'currentColor' }: { kind: FileKind; size?: number; color?: string }) {
  const stroke = color;
  const common = {
    width: size, height: size, viewBox: '0 0 24 24',
    fill: 'none', stroke, strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (kind) {
    case 'image':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="9" cy="10" r="1.4" />
          <path d="M3 17l5-5 4 4 3-3 6 6" />
        </svg>
      );
    case 'video':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M10 9.5l5 2.5-5 2.5z" fill={stroke} />
        </svg>
      );
    case 'audio':
      return (
        <svg {...common}>
          <path d="M9 18V6l11-2v12" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="17" cy="16" r="3" />
        </svg>
      );
    case 'text':
      return (
        <svg {...common}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h4" />
        </svg>
      );
    case 'pdf':
      return (
        <svg {...common}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M8 14h1.2a1.3 1.3 0 0 1 0 2.6H8V14zm0 2.6V18m4-4.6h2.6M12.4 16h2M16 14h1.6a1.4 1.4 0 0 1 0 2.8H16V14z" strokeWidth="1.2" />
        </svg>
      );
    case 'archive':
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M10 3v3M14 3v3M10 9v2M14 9v2M10 13v2M14 13v2M10 17h4v3h-4z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
        </svg>
      );
  }
}

export const Page = styled.div`
  min-height: 100vh;
  padding: 28px 18px;
  background:
    radial-gradient(900px 700px at 18% -10%, rgba(200,173,106,0.10), transparent 62%),
    radial-gradient(800px 520px at 110% 18%, rgba(200,173,106,0.07), transparent 56%),
    ${({ theme }) => theme.colors.bg0};

  @media (max-width: 980px) {
    padding-top: 84px;
  }
`;

export const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

export const Topbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;

  @media (max-width: 980px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    margin: 0;
    padding: 14px 18px;
    background: rgba(11,11,15,0.72);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  }
`;

export const Brand = styled.div`
  font-weight: 700;
  letter-spacing: 0.4px;
`;

export const Badge = styled.span`
  display: inline-block;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.muted};
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: rgba(0,0,0,0.22);
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 18px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const Sidebar = styled.div<{ open?: boolean }>`
  position: sticky;
  top: 18px;
  align-self: start;

  @media (max-width: 980px) {
    display: ${({ open }) => (open ? 'block' : 'none')};
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    z-index: 49;
    background: rgba(11,11,15,0.96);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
    padding: 16px;
    max-height: calc(100vh - 60px);
    overflow: auto;

    && > * {
      background: transparent;
      border: none;
      box-shadow: none;
      padding: 0;
    }
  }
`;

export const NavToggle = styled.button<{ active?: boolean }>`
  display: none;
  position: fixed;
  top: 22px;
  right: 18px;
  z-index: 60;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;

  span {
    display: block;
    width: 20px;
    height: 1.5px;
    background: ${({ theme }) => theme.colors.text};
    transition: all 0.3s;
  }

  ${({ active }) =>
    active &&
    `
    span:first-child { transform: rotate(45deg) translate(2.5px, 2.5px); }
    span:last-child  { transform: rotate(-45deg) translate(2.5px, -2.5px); }
  `}

  @media (max-width: 980px) {
    display: flex;
  }
`;

export const MobileHide = styled.div<{ active?: boolean }>`
  ${({ active }) =>
    active &&
    `
    @media (max-width: 980px) {
      display: none !important;
    }
  `}
`;

export const MobileMenuBackdrop = styled.div<{ open?: boolean }>`
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 48;
  background: rgba(11,11,15,0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  transition: opacity 0.25s ease;

  @media (max-width: 980px) {
    display: block;
  }
`;

export const Nav = styled.div`
  display: grid;
  gap: 8px;
`;

export const NavLink = styled.a<{ active?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ active, theme }) => (active ? theme.colors.lineGold : theme.colors.line)};
  background: ${({ active }) => (active ? 'rgba(200,173,106,0.10)' : 'rgba(0,0,0,0.18)')};
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    text-decoration: none;
    border-color: ${({ theme }) => theme.colors.lineGold};
    background: rgba(200,173,106,0.10);
  }
`;

export const Main = styled.div`
  display: grid;
  gap: 18px;
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background:
    radial-gradient(900px 760px at 18% -10%, rgba(200,173,106,0.12), transparent 62%),
    rgba(11,12,15,0.92);
  backdrop-filter: blur(10px);
  display: grid;
  place-items: center;
  padding: 22px;
`;

export const Modal = styled.div`
  width: min(760px, 100%);
  max-height: min(86vh, 900px);
  overflow: auto;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.panel}, ${({ theme }) => theme.colors.panel2});
  box-shadow: ${({ theme }) => theme.shadow.soft};
  padding: 22px;
`;

export const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

export const StepTitle = styled.div`
  font-weight: 900;
  letter-spacing: 0.2px;
  font-size: 22px;
`;

export const StepPills = styled.div`
  display: grid;
  gap: 8px;
  justify-items: end;
`;

export const ProgressWrap = styled.div`
  width: 220px;
  height: 6px;
  border-radius: 999px;
  background: rgba(255,255,255,0.06);
  border: 1px solid ${({ theme }) => theme.colors.line};
  overflow: hidden;
`;

export const ProgressBar = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => Math.max(0, Math.min(100, pct))}%;
  background: linear-gradient(90deg, rgba(200,173,106,0.55), rgba(200,173,106,0.18));
  transition: width 280ms ease;
`;

export const Pill = styled.span<{ active?: boolean }>`
  display: none;
`;

export const Spinner = styled.div<{ size?: number }>`
  width: ${({ size }) => (size ?? 14)}px;
  height: ${({ size }) => (size ?? 14)}px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.18);
  border-top-color: rgba(200,173,106,0.9);
  animation: spin 900ms linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export const Card = styled.div`
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.panel}, ${({ theme }) => theme.colors.panel2});
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 16px;
  box-shadow: ${({ theme }) => theme.shadow.soft};
`;

export const CardTitle = styled.h2`
  font-size: 14px;
  margin: 0 0 10px;
  font-weight: 800;
  letter-spacing: 0.25px;
  color: ${({ theme }) => theme.colors.text};
`;

export const Muted = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 13px;
  line-height: 1.45;
`;

export const Field = styled.div`
  display: grid;
  gap: 10px;
  margin: 10px 0;
`;

export const Label = styled.label`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.muted};
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid rgba(210,180,91,0.20);
  background: rgba(11,13,16,0.55);
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &:focus {
    border-color: rgba(210,180,91,0.55);
    box-shadow: 0 0 0 3px rgba(210,180,91,0.12);
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid rgba(210,180,91,0.20);
  background: rgba(11,13,16,0.55);
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  resize: vertical;

  &:focus {
    border-color: rgba(210,180,91,0.55);
    box-shadow: 0 0 0 3px rgba(210,180,91,0.12);
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid rgba(210,180,91,0.20);
  background: rgba(11,13,16,0.55);
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &:focus {
    border-color: rgba(210,180,91,0.55);
    box-shadow: 0 0 0 3px rgba(210,180,91,0.12);
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

export const Button = styled.button<{ variant?: 'primary' | 'ghost' | 'danger' }>`
  position: relative;
  overflow: hidden;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ variant, theme }) =>
    variant === 'danger' ? 'rgba(255,92,122,0.55)' : theme.colors.lineGold};

  background: ${({ variant }) =>
    variant === 'danger'
      ? 'transparent'
      : variant === 'ghost'
        ? 'transparent'
        : 'linear-gradient(135deg, rgba(200,173,106,0.22), rgba(173,139,63,0.12))'};

  color: ${({ variant, theme }) => (variant === 'danger' ? theme.colors.danger : variant === 'ghost' ? theme.colors.text : theme.colors.gold)};
  font-weight: 800;
  letter-spacing: 0.25px;
  cursor: pointer;
  outline: none;
  transform: translateY(0);
  transition: transform 180ms ease, border-color 220ms ease, filter 220ms ease, box-shadow 220ms ease;

  &:focus-visible {
    border-color: rgba(210,180,91,0.55);
    box-shadow: 0 0 0 3px rgba(210,180,91,0.12);
  }

  /* Minimal luxury shimmer on primary (slow + subtle) */
  &::before {
    content: '';
    position: absolute;
    inset: -50% -120%;
    background: linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.10) 50%, transparent 65%);
    transform: translateX(-40%);
    opacity: ${({ variant }) => (variant === 'ghost' || variant === 'danger' ? 0 : 1)};
    transition: transform 900ms ease;
    pointer-events: none;
  }

  &:hover {
    border-color: ${({ variant }) => (variant === 'danger' ? 'rgba(255,92,122,0.85)' : 'rgba(200,173,106,0.38)')};
    background: ${({ variant }) => (variant === 'danger' ? 'rgba(255,92,122,0.08)' : undefined)};
    transform: translateY(-1px);
    filter: brightness(1.03);
  }

  &:hover::before {
    transform: translateX(40%);
  }

  &:active {
    transform: translateY(0);
    filter: brightness(1);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Hr = styled.hr`
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  margin: 14px 0;
`;

export const MonoBox = styled.pre`
  margin: 0;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid rgba(210,180,91,0.16);
  background: rgba(11,13,16,0.55);
  color: ${({ theme }) => theme.colors.text};
  overflow: auto;
  font-size: 12px;
  line-height: 1.45;
`;
